import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import './reports.css';

const SalesInvoices = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchSalesInvoices();
    }
  }, [dateRange, token, i18n.language]);

  const fetchSalesInvoices = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/sales_invoices_report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }),
      });

      const result = await response.json();
      if (result.status) {
        setInvoices(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch invoices');
      }
    } catch (error) {
      setError('Error fetching invoices');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  const calculateTotals = () => {
    const totals = invoices.reduce((acc, invoice) => {
      acc.totalInvoices += 1;
      acc.totalAmount += parseFloat(invoice.grand_total) || 0;
      acc.totalVAT += parseFloat(invoice.vat) || 0;
      acc.totalSubtotal += parseFloat(invoice.subtotal) || 0;
      return acc;
    }, {
      totalInvoices: 0,
      totalAmount: 0,
      totalVAT: 0,
      totalSubtotal: 0
    });

    return totals;
  };

  if (loading) {
    return (
      <div className="container tables bg-white mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('reports.salesInvoices.title')}</h3>
      </div>

      <div className="report-filters">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">{t('reports.startDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('reports.endDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {invoices.length > 0 && (
        <div className="report-summary">
          <div className="row">
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalInvoices')}</h5>
                <span className="summary-number">{totals.totalInvoices}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalAmount')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalAmount)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalVAT')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalVAT)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalSubtotal')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalSubtotal)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>{t('reports.invoiceNumber')}</th>
              <th>{t('reports.invoiceDate')}</th>
              <th>{t('reports.customerName')}</th>
              <th>{t('reports.subtotal')}</th>
              <th>{t('reports.vat')}</th>
              <th>{t('reports.grandTotal')}</th>
              <th>{t('reports.paymentMethod')}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  {t('reports.noInvoices')}
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.invoice_number}</td>
                  <td>{formatDate(invoice.invoice_date)}</td>
                  <td>{invoice.customer_name}</td>
                  <td>{formatCurrency(invoice.subtotal)}</td>
                  <td>{formatCurrency(invoice.vat)}</td>
                  <td>{formatCurrency(invoice.grand_total)}</td>
                  <td>{t(`invoices.${invoice.payment_method}`)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesInvoices;
